<template>
  <div class="group relative" @mouseenter="refreshStatus">
    <button
      type="button"
      class="flex flex-row items-center justify-center gap-2 rounded-md bg-primary px-3 py-2 font-bold text-contrast-primary hover:bg-primary-600"
      :aria-label="$t('global.modal.support.help')"
      @click="openModal"
    >
      <span class="relative flex">
        <FontAwesomeIcon :icon="['fal', 'circle-question']" size="lg" />
        <span
          class="absolute -top-0.5 -right-0.5 size-2.5 rounded-full ring-2 ring-white"
          :class="{ 'bg-green-500': supportOpen, 'bg-red-500': !supportOpen }"
        />
      </span>
      <span class="capitalize-first">
        {{ $t('global.modal.support.help') }}
      </span>
    </button>
    <!-- Status preview on hover, the padding bridges the gap to the button -->
    <div
      v-if="!open"
      class="absolute top-full right-0 z-50 hidden w-80 pt-2 group-hover:block"
    >
      <div
        class="flex flex-col gap-1.5 rounded-lg border bg-white p-3 text-sm shadow-lg dark:border-slate-600 dark:bg-slate-700 dark:text-gray-300"
      >
        <div class="flex flex-row items-center gap-2 font-bold">
          <span
            class="size-2 shrink-0 rounded-full"
            :class="{ 'bg-green-500': supportOpen, 'bg-red-500': !supportOpen }"
          />
          <span class="capitalize-first">{{ statusLabel }}</span>
        </div>
        <div
          v-if="supportOpen"
          class="text-gray-600 capitalize-first dark:text-gray-400"
        >
          {{ $t('global.modal.support.status_online_description') }}
        </div>
        <div class="flex flex-row items-center gap-2">
          <FontAwesomeIcon :icon="['fal', 'clock']" fixed-width />
          <span class="capitalize-first">
            {{ $t('global.modal.support.opening_hours') }}
          </span>
        </div>
        <div class="flex flex-row items-center gap-2 font-semibold">
          <FontAwesomeIcon :icon="['fal', 'phone']" fixed-width />
          <span class="capitalize-first">{{ emergencyLabel }}</span>
        </div>
      </div>
    </div>
  </div>
  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" class="relative z-100" @close="open = false">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-3">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-xl transform overflow-hidden rounded-xl bg-white p-6 pt-5 text-left align-middle shadow-xl transition-all dark:bg-slate-700 dark:text-gray-300"
            >
              <div class="flex flex-row items-start">
                <div class="flex flex-1 flex-col items-start gap-2">
                  <DialogTitle
                    as="h3"
                    class="text-xl leading-6 font-bold text-primary capitalize-first dark:text-gray-100"
                  >
                    {{ $t('global.modal.support.need_help') }}
                  </DialogTitle>
                  <div
                    class="flex flex-row flex-wrap items-center gap-x-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                    :class="{
                      'bg-green-50 text-green-800 dark:bg-green-900/40 dark:text-green-200':
                        supportOpen,
                      'bg-red-50 text-red-800 dark:bg-red-900/40 dark:text-red-200':
                        !supportOpen
                    }"
                  >
                    <span
                      class="size-1.5 shrink-0 rounded-full"
                      :class="{
                        'bg-green-500': supportOpen,
                        'bg-red-500': !supportOpen
                      }"
                    />
                    <span class="capitalize-first">{{ statusLabel }}</span>
                    <span aria-hidden="true">·</span>
                    <span class="capitalize-first">
                      {{
                        supportOpen
                          ? $t('global.modal.support.status_online_description')
                          : emergencyLabel
                      }}
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  class="shrink-0 rounded-md text-gray-700 hover:text-gray-900 focus:outline-none dark:text-gray-300"
                  :aria-label="$t('button.close')"
                  @click="open = false"
                >
                  <FontAwesomeIcon :icon="['fal', 'times']" size="xl" />
                </button>
              </div>

              <div class="mt-5 flex flex-col gap-2">
                <component
                  :is="action.to ? 'RouterLink' : 'a'"
                  v-for="(action, index) in actions"
                  :key="action.key"
                  v-bind="
                    action.to ? { to: action.to } : linkAttributes(action)
                  "
                  class="flex flex-row items-center gap-3 rounded-lg border p-3 transition-colors"
                  :class="{
                    'border-transparent bg-primary text-contrast-primary hover:bg-primary-600':
                      index === 0,
                    'hover:border-primary-300 hover:bg-primary-50 dark:border-slate-600 dark:hover:bg-slate-600':
                      index !== 0
                  }"
                  @click="action.to && (open = false)"
                >
                  <span
                    class="flex size-10 shrink-0 items-center justify-center rounded-full"
                    :class="{
                      'bg-white/15': index === 0,
                      'bg-primary-50 text-primary dark:bg-slate-600 dark:text-primary-300':
                        index !== 0
                    }"
                  >
                    <FontAwesomeIcon :icon="['fal', action.icon]" size="lg" />
                  </span>
                  <span class="flex flex-1 flex-col">
                    <span class="font-bold capitalize-first">
                      {{ action.title }}
                    </span>
                    <span
                      class="text-sm capitalize-first"
                      :class="{
                        'opacity-90': index === 0,
                        'text-gray-600 dark:text-gray-400': index !== 0
                      }"
                    >
                      {{ action.description }}
                    </span>
                  </span>
                  <FontAwesomeIcon
                    v-if="index === 0"
                    :icon="['fal', 'arrow-right']"
                    class="shrink-0"
                  />
                </component>
              </div>

              <div
                class="mt-4 flex flex-col items-center gap-1.5 text-gray-600 dark:text-gray-400"
              >
                <div
                  class="flex flex-row flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs"
                >
                  <span
                    class="flex flex-row items-center gap-1.5 whitespace-nowrap"
                  >
                    <FontAwesomeIcon :icon="['fal', 'clock']" />
                    <span class="capitalize-first">
                      {{ $t('global.modal.support.opening_hours') }}
                    </span>
                  </span>
                  <span
                    v-if="supportOpen"
                    class="flex flex-row items-center gap-1.5 whitespace-nowrap"
                  >
                    <FontAwesomeIcon :icon="['fal', 'message-lines']" />
                    <span class="capitalize-first">
                      {{ $t('global.modal.support.usual_response_time') }}
                    </span>
                  </span>
                </div>
                <a
                  :href="phone.href"
                  class="flex flex-row items-center gap-1.5 text-sm font-bold text-gray-900 hover:underline dark:text-white"
                >
                  <FontAwesomeIcon :icon="['fal', 'phone']" />
                  <span class="capitalize-first">{{ emergencyLabel }}</span>
                </a>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
  DialogTitle
} from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapGetters } from 'vuex'
import {
  helpCenterLinks,
  isSupportOpen,
  supportPhone
} from '@/views/layouts/top-bar/components/helpCenter'

export default {
  name: 'HelpMeModal',
  components: {
    FontAwesomeIcon,
    Dialog,
    DialogTitle,
    DialogPanel,
    TransitionRoot,
    TransitionChild
  },
  data() {
    return {
      open: false,
      supportOpen: isSupportOpen()
    }
  },
  computed: {
    ...mapGetters({
      releaseVersion: 'metadata/getReleaseVersion'
    }),
    phone() {
      return supportPhone(this.$i18n.locale)
    },
    statusLabel() {
      return this.supportOpen
        ? this.$t('global.modal.support.status_online')
        : this.$t('global.modal.support.status_closed')
    },
    emergencyLabel() {
      return this.$t('global.modal.support.emergency_only', {
        phone: this.phone.label
      })
    },
    actions() {
      const links = helpCenterLinks(this.$i18n.locale)
      return [
        {
          key: 'submit_request',
          icon: 'message-lines',
          href: links.submitRequest,
          title: this.$t('global.modal.support.submit_request'),
          description: this.$t(
            'global.modal.support.submit_request_description'
          )
        },
        {
          key: 'my_requests',
          icon: 'inbox',
          href: links.myRequests,
          title: this.$t('global.modal.support.my_requests'),
          description: this.$t('global.modal.support.my_requests_description')
        },
        {
          key: 'knowledge_base',
          icon: 'book-open',
          href: links.knowledgeBase,
          title: this.$t('global.modal.support.knowledge_base'),
          description: this.$t(
            'global.modal.support.knowledge_base_description'
          )
        },
        {
          key: 'whats_new',
          icon: 'bullhorn',
          to: { name: 'settings_version_note_index_route' },
          title: this.$t('global.modal.support.whats_new'),
          description: this.$t('global.modal.support.whats_new_description', {
            version: this.releaseVersion ?? ''
          })
        }
      ]
    }
  },
  mounted() {
    // Keeps the dot on the button in sync when the opening hours start or end
    this.statusTimer = setInterval(this.refreshStatus, 60 * 1000)
  },
  beforeUnmount() {
    clearInterval(this.statusTimer)
  },
  methods: {
    refreshStatus() {
      this.supportOpen = isSupportOpen()
    },
    openModal() {
      this.refreshStatus()
      this.open = true
    },
    linkAttributes(action) {
      return { href: action.href, target: '_blank', rel: 'noopener' }
    }
  }
}
</script>
